/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useGetMainMenu} from '../../custom-api/hooks/use-get-main-menu'
import {motion, AnimatePresence} from 'framer-motion'
import {useLocation} from 'react-router-dom'

// Components
import {
    Drawer,
    DrawerBody,
    DrawerContent,
    Text,
    Flex,
    useMultiStyleConfig,
    Portal,
    Box,
} from '@chakra-ui/react'
import {ChevronLeftIcon, CloseIcon} from '../icons'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink} from '../../utils/contentstack-utils'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {DrawerMenuSecondLevel} from './drawer-menu-second-level'
import {DrawerMenuFirstLevel} from './drawer-menu-first-level'
import {remCalc} from '../../theme/_utils'
// import {data} from './mock'

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */

const DrawerMenu = ({isOpen, onClose = noop, onWishlistClick}) => {
    const [ariaBusy, setAriaBusy] = useState('true')
    const [selectedPrimaryCategoryId, setSelectedPrimaryCategoryId] = useState(null)
    const location = useLocation()

    const styles = useMultiStyleConfig('KenwoodMenu')
    const {data} = useGetMainMenu('kenwood_')

    const categoriesData = useGetCategoriesData()

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    //used to close the drawer every time the user navigates to another page
    useEffect(() => {
        if (isOpen) {
            onClose()
        }
    }, [location])

    const selectedCategory = data?.primarycategories.find(
        (primarycategory) => primarycategory.uid === selectedPrimaryCategoryId,
    )

    const categoryLink =
        categoriesData && getGlobalLink({link: selectedCategory?.redirects, categoriesData})

    const onDrawerClose = () => {
        setSelectedPrimaryCategoryId(null)
        onClose()
    }

    const variants = {
        initial: {x: '100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '100%', transition: {duration: 0.3}},
    }

    const reverseVariants = {
        initial: {x: '-100%'},
        enter: {x: 0, transition: {duration: 0.3}},
        exit: {x: '-100%', transition: {duration: 0.3}},
    }

    const [drawerMenuTopMargin, setDrawerMenuTopMargin] = useState(0)

    useEffect(() => {
        if (typeof document === 'undefined') return

        const header = document.getElementById('main-header')
        const sovraHeader = document.getElementById('sovra-header')

        function updateHeaderHeight() {
            let sovraHeaderHeight = 0

            if (sovraHeader) {
                const rect = sovraHeader.getBoundingClientRect()
                if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
                    sovraHeaderHeight = rect.height
                }
            }

            const headerHeight = header ? header.offsetHeight : 0
            setDrawerMenuTopMargin(headerHeight + sovraHeaderHeight)
        }

        updateHeaderHeight()

        window.addEventListener('scroll', updateHeaderHeight)
        window.addEventListener('resize', updateHeaderHeight)

        return () => {
            window.removeEventListener('scroll', updateHeaderHeight)
            window.removeEventListener('resize', updateHeaderHeight)
        }
    }, [])

    const headerLinks = data?.headerlinks ? data?.headerlinks?.[0]?.links : []

    return (
        <Drawer
            isOpen={isOpen}
            // reset selectedId when drawer is closed from the parent component
            onCloseComplete={() => setSelectedPrimaryCategoryId(null)}
            onClose={onDrawerClose}
            placement="left"
            size="full"
            blockScrollOnMount={false} // need to check if scrolling on old IOS is still an issue without this
        >
            <DrawerContent
                overflow="scroll"
                w="100vw"
                maxW="100vw"
                mt={drawerMenuTopMargin}
                transitionProperty={'margin-top'}
                transitionDuration={'0.1s'}
                transitionTimingFunction={'linear'}
                pb={remCalc(91)}
                // Safari 15.4 and below doesn't support 100dvh, so we need to set the height to 100vh
                sx={{
                    position: 'static',
                    height: '100vh',
                    maxHeight: '100vh',
                    '@supports (height: 100dvh)': {
                        height: '100dvh',
                        maxHeight: '100dvh',
                    },
                }}
            >
                <DrawerBody
                    sx={{
                        px: {base: remCalc(16), md: remCalc(25)},
                        pt: {base: remCalc(24), md: remCalc(40)},
                        // and add extra padding to be able to click the last items
                        pb: {base: remCalc(84), sm: remCalc(94), md: remCalc(94)},
                        // if 100dvh is supported, then we use the original padding
                        '@supports (height: 100dvh)': {
                            pb: {base: remCalc(16), md: remCalc(40)},
                        },
                        WebkitOverflowScrolling: 'touch',
                        overscrollBehavior: 'contain',
                    }}
                    flex="1"
                    overflowY="auto"
                    onTouchMove={(e) => {
                        e.stopPropagation() // prevent event from reaching body
                    }}
                >
                    <div
                        id="category-nav"
                        aria-live="polite"
                        aria-busy={ariaBusy}
                        aria-atomic="true"
                    >
                        <AnimatePresence initial={false} mode="popLayout">
                            {data && categoriesData && (
                                <>
                                    {selectedCategory && (
                                        <Portal>
                                            <motion.div
                                                key="second-level"
                                                initial="initial"
                                                animate="enter"
                                                exit="exit"
                                                variants={variants}
                                                style={{
                                                    position: 'fixed',
                                                    width: '100%',
                                                    top: 0,
                                                    left: 0,
                                                    bottom: 0,
                                                    right: 0,
                                                    zIndex: 9999,
                                                    backgroundColor: 'white',
                                                    padding: remCalc(16),
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                <Box p={{base: remCalc(0), md: remCalc(8)}}>
                                                    <Flex
                                                        onClick={() =>
                                                            setSelectedPrimaryCategoryId(null)
                                                        }
                                                        {...{
                                                            justifyContent: 'space-between',
                                                            px: {base: remCalc(8)},
                                                        }}
                                                    >
                                                        <Flex {...styles.breadcrumbContainer}>
                                                            {selectedPrimaryCategoryId && (
                                                                <>
                                                                    <ChevronLeftIcon
                                                                        boxSize="iconsSize.medium"
                                                                        color="button.text.icon-hover"
                                                                    />
                                                                    <Text
                                                                        {...styles.breadcrumbText}
                                                                        textStyle="body-medium"
                                                                        fontWeight={600}
                                                                    >
                                                                        {categoryLink?.label}
                                                                    </Text>
                                                                </>
                                                            )}
                                                        </Flex>
                                                        <CloseIcon />
                                                    </Flex>
                                                    <DrawerMenuSecondLevel
                                                        categoriesData={categoriesData}
                                                        categoryUrl={categoryLink?.url}
                                                        secondLevel={selectedCategory?.second_level}
                                                        onClose={onClose}
                                                    />
                                                </Box>
                                            </motion.div>
                                        </Portal>
                                    )}
                                    <motion.div
                                        key="first-level"
                                        initial="initial"
                                        animate="enter"
                                        exit="exit"
                                        variants={reverseVariants}
                                    >
                                        <DrawerMenuFirstLevel
                                            primarycategories={data.primarycategories}
                                            setSelectedPrimaryCategoryId={
                                                setSelectedPrimaryCategoryId
                                            }
                                            categoriesData={categoriesData}
                                            onWishlistClick={onWishlistClick}
                                            headerLinks={headerLinks}
                                        />
                                    </motion.div>
                                </>
                            )}
                        </AnimatePresence>
                    </div>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    onWishlistClick: PropTypes.func,
}

export {DrawerMenu}
